import { useCallback, useSyncExternalStore } from 'react';

// useMediaQuery — react to a CSS media query (PRD §9.5 +
// docs/frontend_architecture.md §6, B2 PR7 / B2g).
//
// Returns the current match state of `query` and re-renders the caller
// whenever it flips. Used by the share viewer to swap between stacked
// (mobile) and side-by-side (≥768px) layouts.
//
// Implemented with `useSyncExternalStore` — the React-canonical way to
// subscribe a component to an external mutable source. The `subscribe`
// callback registers a `change` listener on the MediaQueryList; the
// `getSnapshot` callback reads the current `matches` value. The third
// "server snapshot" arg returns `false` so SSR / non-DOM environments
// render the mobile-first layout (the fallback that always at least
// fits). useCallback keeps both callbacks stable across renders for
// the same `query` so useSyncExternalStore doesn't tear down and
// re-establish the listener on every parent re-render.

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (cb: () => void): (() => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => {
          // No-op cleanup for environments without matchMedia.
        };
      }
      const mql = window.matchMedia(query);
      mql.addEventListener('change', cb);
      return () => {
        mql.removeEventListener('change', cb);
      };
    },
    [query]
  );

  const getSnapshot = useCallback((): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(query).matches;
  }, [query]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
